/*
 * One referral, opened from the Referrals table: the lead's details and note,
 * how it reached Wealthoak, where it stands, and, once converted, the client's
 * plan and what they have earned. Only fields GET /referrals already returns.
 */

import type { Referral } from "../api/client";
import { Dialog, REFERRAL_STATUS, Status, accountStatus, subscriptionStatus } from "./bits";
import { fmtDate, fmtDateTime, inr } from "./format";
import "./clients.css";

const SOURCE_TEXT = {
  partner: "You referred them",
  link: "They joined through your referral link",
  credit: "An add-user request you made",
  staff: "Added by Wealthoak",
} as const;

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

export default function ReferralSheet({ r, onClose }: { r: Referral; onClose: () => void }) {
  const st = REFERRAL_STATUS[r.status];
  const c = r.client;
  return (
    <Dialog
      variant="sheet"
      title={r.lead_name}
      description={<Status tone={st.tone}>{st.label}</Status>}
      onClose={onClose}
    >
      <dl className="kv client-kv">
        <Row label="Email">{r.lead_email}</Row>
        {r.lead_phone && <Row label="Phone">{r.lead_phone}</Row>}
        <Row label="How they came">{SOURCE_TEXT[r.source ?? "partner"]}</Row>
        <Row label="Referred">{fmtDateTime(r.created_at)}</Row>
        {r.converted_at && <Row label="Became a client">{fmtDateTime(r.converted_at)}</Row>}
        {r.notes && <Row label="Your note">{r.notes}</Row>}
        {r.status === "rejected" && (
          <Row label="Why not">
            {r.status_reason ?? "No reason given."}
            {r.source === "credit" && <small>Your add-user credit was returned.</small>}
          </Row>
        )}
      </dl>

      {c && (
        <>
          <h3 className="client-sheet__heading">Client</h3>
          <dl className="kv client-kv">
            <Row label="Account">
              <Status tone={accountStatus(c.account_status).tone}>{accountStatus(c.account_status).label}</Status>
            </Row>
            <Row label="Plan">
              <span className={`chip${c.plan_name ? " is-gold" : ""}`}>{c.plan_name ?? "No plan yet"}</span>
            </Row>
            <Row label="Subscription">
              <Status tone={subscriptionStatus(c.subscription_status).tone}>
                {subscriptionStatus(c.subscription_status).label}
              </Status>
              {c.subscribed_until && <small>Until {fmtDate(c.subscribed_until)}</small>}
            </Row>
            <Row label="Commission earned">
              <strong>{inr(r.commission_total)}</strong>
            </Row>
          </dl>
        </>
      )}
    </Dialog>
  );
}
